import * as React from "react";

import { setCanvasHtml } from "@v1/components/canvas/canvas-reducer";
import { resetProductData } from "@v1/reducers/commerce-reducer";
import { selectProjectId, setProjectData } from "@v1/reducers/core-reducer";
import { getFieldMapping } from "@v1/reducers/utils/core-reducer-utils";
import { useEditorDispatch, useEditorStore } from "@v1/store/store";
import {
  getLastSelectedElementIdForProject,
  saveCurrentElementId,
} from "@v1/utils/localStorage";
import { useCurrentProjectContext } from "@v2/contexts/CurrentProjectContext";
import { elementUtils } from "@v2/trpc/routes/element";
import { projectUtils } from "@v2/trpc/routes/project";
import { routes } from "@v2/utils/router";
import mapValues from "lodash-es/mapValues";
import { matchPath, useNavigate, useSearchParams } from "react-router-dom";
import { getFromRecordOrNull } from "replo-runtime/shared/utils/optional";

/**
 * NOTE (Ben, 2026-02-10): Effect-only component (renders null) that handles v1 Redux initialization
 * when a project loads. This includes:
 * - Resetting product data and canvas HTML when switching projects
 * - Selecting the draft element and dispatching setProjectData
 * - Navigating to the correct element URL
 *
 * Follows the same pattern as GlobalHotkeysListener, MetafieldsAutoFetcher, CustomFontLoader.
 */
const CurrentProjectV1Initializer: React.FC = () => {
  const { project, projectId, elementMetadata, isElementMetadataSuccess } =
    useCurrentProjectContext();
  const dispatch = useEditorDispatch();
  const store = useEditorStore();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Effect A: Project switch cleanup — reset product data and canvas HTML
  // when the user switches to a different project.
  React.useEffect(() => {
    const existingProjectId = selectProjectId(store.getState());

    // NOTE (Fran 2024-08-23): Reinitialize the project and reset all product data whenever
    // the user switches to a different project.
    if (existingProjectId !== projectId) {
      dispatch(resetProductData());
      dispatch(setCanvasHtml(""));
      void elementUtils.getById.invalidate();
      void elementUtils.listAllElementsMetadata.invalidate();
      void projectUtils.getV2.invalidate();
    }
  }, [projectId, store, dispatch]);

  // Effect B: Draft element selection + navigation + Redux init.
  // Determines which element to select as draft and dispatches setProjectData.
  React.useEffect(() => {
    // NOTE (Fran 2024-08-23): Initialize the project only once when data is available.
    // Re-initialization occurs only if the project changes.
    if (
      project &&
      elementMetadata &&
      isElementMetadataSuccess &&
      window.location.pathname.includes("/editor/")
    ) {
      const elementMetadataMapping = getFieldMapping(
        elementMetadata ?? [],
        "id",
      );
      const versionMapping = mapValues(
        elementMetadataMapping,
        (value) => value.version,
      );

      let pathMatch = null;
      pathMatch = matchPath(routes.editor.element, window.location.pathname);

      const paramElementId = pathMatch?.params?.elementId || null;

      const localStorageElementId = paramElementId
        ? null
        : getLastSelectedElementIdForProject(project.id);

      const validLocalStorageId =
        localStorageElementId &&
        elementMetadataMapping[localStorageElementId]?.projectId === project.id;

      const draftElementIdFromUrlOrLocalStorage =
        paramElementId || (validLocalStorageId ? localStorageElementId : null);

      const draftElement =
        getFromRecordOrNull(
          elementMetadataMapping,
          draftElementIdFromUrlOrLocalStorage,
        ) ??
        elementMetadata?.[0] ??
        null;

      if (localStorageElementId !== draftElement?.id && draftElement) {
        saveCurrentElementId(
          draftElement.id,
          elementMetadataMapping[draftElement.id]!.projectId,
        );
      }

      dispatch(
        setProjectData({
          project,
          draftElementId: draftElement?.id,
          versionMapping,
        }),
      );

      const isNewElementPath = matchPath(
        "/editor/:projectId/new",
        window.location.pathname,
      );

      // NOTE (Ben O., 2026-02-06): navigateToDefaultElementAfterFetch is derived
      // from the URL — only /editor/ routes should navigate to default elements.
      const navigateToDefaultElementAfterFetch =
        window.location.pathname.includes("/editor/");

      if (!draftElement) {
        const path = `/editor/${project.id}/new`;

        navigate(path, {
          replace: true,
        });
      } else if (
        navigateToDefaultElementAfterFetch &&
        !isNewElementPath &&
        !paramElementId
      ) {
        const path = `/editor/${project.id}/${draftElement.id}`;

        // Preserve all existing search params
        const searchString = searchParams.toString();
        const targetPath = searchString ? `${path}?${searchString}` : path;

        navigate(targetPath, {
          replace: true,
        });
      }
    }
  }, [
    project,
    elementMetadata,
    isElementMetadataSuccess,
    dispatch,
    navigate,
    searchParams,
  ]);

  return null;
};

export default CurrentProjectV1Initializer;
